/**
 * The application surface. One object, at every viewport, and nothing rendered outside it.
 *
 * Below `md` it is the screen: one column, edge to edge, the cart behind the total bar. From
 * `md` (768px) a tablet fills its screen with two panels — search on the left, the sale on the
 * right — one surface, one header and one nav spanning both. From `lg` (1024px) the only thing
 * that changes is that it stops growing: capped at `max-w-wide`, centred, on a backdrop, so it
 * does not stretch absurdly on a monitor. A landscape tablet at ~1180px lands in that range,
 * which is right. Shop, user and today's figures live inside it, on Profile, where a phone
 * already keeps them; nothing floats beside the app at any width.
 *
 * ## Why the height is fixed
 *
 * The column is `h-dvh`, not `min-h-dvh`, and `<main>` is the only thing that scrolls. With a
 * minimum height the column grew to fit long content and carried the bottom nav off the screen
 * with it — which is what happened on Profile and Payment. A fixed height means the nav is a
 * flex sibling pinned by geometry, not by `position: fixed`, so nothing ever has to reserve
 * space for it and the last item in any list is never hidden behind it.
 */

import { useEffect, type ReactNode } from 'react';

import { applyBrand, applyShopTheme, selectTheme, selectUser } from '@pos/shared';
import { neutralBrand } from '@pos/tokens';

import { useAppSelector } from '@/app/hooks';

import { ShellHeader } from './ShellHeader.js';
import { ShellNav } from './ShellNav.js';
import { OfflineBanner } from './OfflineBanner.js';
import { SessionExpiryBanner } from './SessionExpiryBanner.js';
import { ShiftBanner } from './ShiftBanner.js';

export function PosShell({ children }: { children: ReactNode }) {
  const user = useAppSelector(selectUser);

  // From the shop the signed-in user belongs to — a salesperson never switches shops. Also keyed
  // on the toggle, so choosing "system" hands the scheme back to the shop's default.
  const themeChoice = useAppSelector(selectTheme);

  // The shop's look and the tab that frames it, in one effect: both answer "which shop is this",
  // and a tab still reading the neutral name over a Diamond till is the same leak as the colour.
  useEffect(() => {
    applyShopTheme(user?.homeShopTheme);
    applyBrand(user?.homeShopName ?? neutralBrand.pos);
  }, [user?.homeShopTheme, user?.homeShopName, themeChoice]);

  return (
    <div className="flex h-dvh justify-center bg-surface-backdrop">
      <div
        className={[
          'relative flex h-full w-full flex-col bg-surface-base',
          // Desktop only: the cap, the margin, the card. A tablet gets none of these — it fills.
          'lg:my-6 lg:h-[calc(100dvh-3rem)] lg:max-w-wide lg:rounded-sheet lg:shadow-column',
          'pt-[env(safe-area-inset-top)]',
        ].join(' ')}
      >
        <OfflineBanner />
        <ShiftBanner />
        <SessionExpiryBanner />
        <ShellHeader user={user} />

        {/* `min-h-0` so this — and only this — scrolls, instead of growing the column. */}
        <main className="scroll-region min-h-0 flex-1">{children}</main>

        <ShellNav />
      </div>
    </div>
  );
}
